import { BadgeV3, ButtonV2, Menu, Spinner, TooltipV2 } from '@cimplico/components';
import { ArrowTopRightOnSquareIcon, PaperClipIcon, XMarkIcon, DocumentPlusIcon } from '@heroicons/react/20/solid';

import Dropzone from '../../components/dropzone/Dropzone';
import { asyncToast } from '../../components/toast/Toast';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useCreateDocument } from '../../queries/use-create-document';
import { useFileRecords } from '../../queries/use-file-records';
import queryClient from '../../query-client';
import { Worksheet } from '../../types/worksheet';
import plural from '../../utilities/plural';
import AttachmentRow from '../worksheet-v2/entries/attachments/AttachmentRow';
import NewRecords from '../worksheet-v2/entries/attachments/NewRecords';

type FileAttachmentsProps = {
    worksheet: Worksheet;
    isOpen: boolean;
    onOpen: (id?: string) => void;
};

function FileAttachments({ worksheet, isOpen, onOpen }: FileAttachmentsProps): JSX.Element {
    const createDocumentQuery = useCreateDocument();

    const files = worksheet.files;

    if (!worksheet.files) {
        return <></>;
    }

    /**
     * Uploads a list of files to the current worksheet.
     *
     * @param files the list of files to upload
     */
    async function handleFileUpload(files: File[]): Promise<void> {
        const done = asyncToast(`Uploading ${files.length} ${plural(files.length, 'file')}`);
        const uploads = [];
        for (const file of files) {
            const uploadPromise = createDocumentQuery.mutateAsync(
                { file, parentFolderId: worksheet.id },
                {
                    onSuccess: () => {
                        done('success', { title: 'New file uploaded!' });
                    },
                    onError: () => {
                        done('error', { title: 'Failed to upload file' });
                    },
                }
            );
            uploads.push(uploadPromise);
        }
        await Promise.allSettled(uploads);
        queryClient.invalidateQueries([worksheet.workpaperId, 'trial-balance']);
        queryClient.invalidateQueries(['other-schedules', worksheet.workpaperId]);
    }

    return (
        <Menu>
            <Menu.Button onClick={(): void => onOpen(isOpen ? undefined : worksheet.id)}>
                <div className="mt-[2px] mx-auto w-max">
                    {files.length > 0 ? (
                        <BadgeV3 leftIcon={<PaperClipIcon />} color="indigo" label={files.length.toString()} rounded />
                    ) : (
                        <PaperClipIcon className="text-gray-700 h-4 w-4 mt-1" />
                    )}
                </div>
            </Menu.Button>
            <Menu.Body isOpen={isOpen}>
                <div className="w-[400px] h-[400px] flex flex-col">
                    <div className="px-2 py-2 h-12 border-b flex items-center">
                        <p className="text-sm font-medium text-gray-600 pl-2">{worksheet.name}</p>
                        <button className="ml-auto h-5 w-5 text-gray-700" onClick={(): void => onOpen()} type="button">
                            <XMarkIcon />
                        </button>
                    </div>
                    <div className="flex-1 overflow-auto p-[1px]">
                        <Dropzone onDrop={handleFileUpload}>
                            <FileList worksheetId={worksheet.id} />
                        </Dropzone>
                    </div>
                    <BottomSection worksheet={worksheet} />
                </div>
            </Menu.Body>
        </Menu>
    );
}

function FileList({ worksheetId }: { worksheetId: string }): JSX.Element {
    const filesQuery = useFileRecords(1, 100, undefined, worksheetId, '', '', '');

    if (filesQuery.isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Spinner />
            </div>
        );
    }
    if (!filesQuery.isSuccess) {
        return <div>a</div>;
    }

    const { records } = filesQuery.data.data;

    if (records.length === 0) {
        return (
            <div className="flex flex-col  h-full justify-center items-center">
                <div className="flex items-center justify-center w-24 h-24 rounded-full bg-gray-100">
                    <DocumentPlusIcon className="h-12 w-12 text-gray-700 opacity-40" />
                </div>
                <p className="text-sm text-gray-700 mt-1 text-center">
                    Drop your files here
                    <br />
                    <span className="text-xs text-gray-600">or use the buttons below</span>
                </p>
            </div>
        );
    }

    return (
        <>
            {records.map((record) => (
                <div key={record.id}>
                    {/* Reuse worksheet entry attachment row */}
                    <AttachmentRow record={record} />
                </div>
            ))}
        </>
    );
}

function BottomSection({ worksheet }: { worksheet: Worksheet }): JSX.Element {
    const files = worksheet.files;
    const { readOnly } = useWorkpaperContext();

    function redirectToFiles(): void {
        window.open(`./files?folder=${worksheet.id}`, '_blank');
    }

    function handleCreate(): void {
        queryClient.invalidateQueries([worksheet.workpaperId, 'trial-balance']);
        queryClient.invalidateQueries(['other-schedules', worksheet.workpaperId]);
    }

    return (
        <div className="bg-gray-50 p-2 flex justify-between items-center h-12 rounded-b-md relative">
            <div>
                <TooltipV2 message="View in files" position="left">
                    <ButtonV2
                        type="hollow"
                        size="sm"
                        rightIcon={<ArrowTopRightOnSquareIcon className="text-gray-600" />}
                        onClick={redirectToFiles}
                    />
                </TooltipV2>
            </div>

            <p className="text-sm text-gray-700 absolute left-1/2 -translate-x-1/2">
                {files.length} {plural(files.length, 'file')}
            </p>
            <NewRecords folderId={worksheet.id} onCreate={handleCreate} disabled={readOnly} />
        </div>
    );
}

export default FileAttachments;
